import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

interface PortalUser {
  id: string;
  email: string;
  displayName: string;
  typ: 'kunde' | 'sub';
  status: 'pending_invite' | 'active' | 'disabled';
  kundeId: string | null;
  subunternehmerId: string | null;
  inviteExpiresAt: string | null;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  failedLoginCount: number;
  createdAt: string;
}

interface KundeRef {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface SubRef {
  id: string;
  firmenname: string;
}

export const Route = createFileRoute('/portal-users/')({
  beforeLoad: () => requireAccess('/api/v1/portal-users'),
  component: PortalUsersPage,
});

function PortalUsersPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['portal-users'],
    queryFn: () => api<{ items: PortalUser[] }>('/api/v1/portal-users'),
  });
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Portal-Benutzer"
        description="Login-Zugaenge fuer externe Hausverwaltungen, Eigentuemer und Subunternehmer. Onboarding-Mail geht an service@busch-gm.de."
        actions={
          <Button onClick={() => setShowForm((s) => !s)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? 'Abbrechen' : '+ Einladen'}
          </Button>
        }
      />

      {showForm && (
        <InviteForm
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['portal-users'] });
          }}
        />
      )}

      {list.isLoading && <div className="text-slate-500">Lade…</div>}
      <ErrorBanner error={list.error} />

      {list.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left w-20">Typ</th>
                <th className="px-4 py-2 text-left w-32">Status</th>
                <th className="px-4 py-2 text-left">Letzter Login</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.data.items.map((u) => (
                <PortalUserRow key={u.id} user={u} />
              ))}
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Noch keine Portal-Benutzer eingeladen.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [typ, setTyp] = useState<'kunde' | 'sub'>('kunde');
  const [kundeId, setKundeId] = useState('');
  const [subunternehmerId, setSubunternehmerId] = useState('');
  const [error, setError] = useState<unknown>(null);

  const kunden = useQuery({
    queryKey: ['kunden-for-invite'],
    queryFn: () => api<{ items: KundeRef[] }>('/api/v1/kunden'),
    enabled: typ === 'kunde',
  });
  const subs = useQuery({
    queryKey: ['subs-for-invite'],
    queryFn: () => api<{ items: SubRef[] }>('/api/v1/subunternehmer'),
    enabled: typ === 'sub',
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { email, displayName, typ };
      if (typ === 'kunde') body['kundeId'] = kundeId;
      else body['subunternehmerId'] = subunternehmerId;
      return api<{ item: PortalUser }>('/api/v1/portal-users', { method: 'POST', body });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (typ === 'kunde' && !kundeId) {
      setError(new Error('Kunde auswaehlen'));
      return;
    }
    if (typ === 'sub' && !subunternehmerId) {
      setError(new Error('Subunternehmer auswaehlen'));
      return;
    }
    save.mutate();
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Typ</label>
          <select
            value={typ}
            onChange={(e) => setTyp(e.target.value as 'kunde' | 'sub')}
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="kunde">Kunde (HV / Eigentuemer)</option>
            <option value="sub">Subunternehmer</option>
          </select>
        </div>
        <div className="md:col-span-2">
          {typ === 'kunde' ? (
            <>
              <label className="text-xs font-medium text-slate-600">Kunde</label>
              <select
                value={kundeId}
                onChange={(e) => setKundeId(e.target.value)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">– bitte waehlen –</option>
                {kunden.data?.items.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.firmenname} ({k.kundennummer})
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className="text-xs font-medium text-slate-600">Subunternehmer</label>
              <select
                value={subunternehmerId}
                onChange={(e) => setSubunternehmerId(e.target.value)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">– bitte waehlen –</option>
                {subs.data?.items.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.firmenname}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Email</label>
          <Input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="kontakt@firma.de"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">Anzeige-Name</label>
          <Input
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Klaus Schmidt"
          />
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Sende…' : 'Einladen + Mail versenden'}
        </Button>
      </div>
    </form>
  );
}

function PortalUserRow({ user }: { user: PortalUser }) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const reinvite = useMutation({
    mutationFn: () => api(`/api/v1/portal-users/${user.id}/reinvite`, { method: 'POST', body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['portal-users'] }),
    onError: (err) => setError(err),
  });
  const disable = useMutation({
    mutationFn: () => api(`/api/v1/portal-users/${user.id}/disable`, { method: 'POST', body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['portal-users'] }),
    onError: (err) => setError(err),
  });

  const statusBadge = {
    pending_invite: 'bg-amber-50 text-amber-700',
    active: 'bg-emerald-50 text-emerald-700',
    disabled: 'bg-slate-100 text-slate-500',
  }[user.status];
  const statusLabel = {
    pending_invite: 'Einladung offen',
    active: 'aktiv',
    disabled: 'deaktiviert',
  }[user.status];

  return (
    <tr className="text-sm">
      <td className="px-4 py-2 font-medium">
        <Link
          to="/portal-users/$id"
          params={{ id: user.id }}
          className="text-brand-700 hover:underline"
        >
          {user.email}
        </Link>
      </td>
      <td className="px-4 py-2">{user.displayName}</td>
      <td className="px-4 py-2 text-slate-600">{user.typ}</td>
      <td className="px-4 py-2">
        <span className={`inline-block px-2 py-0.5 rounded text-xs ${statusBadge}`}>
          {statusLabel}
        </span>
        {user.lockedUntil && new Date(user.lockedUntil).getTime() > Date.now() && (
          <div className="text-xs text-red-600 mt-1">gesperrt bis {fmtDate(user.lockedUntil)}</div>
        )}
      </td>
      <td className="px-4 py-2 text-slate-600">
        {user.lastLoginAt ? fmtDate(user.lastLoginAt) : '–'}
      </td>
      <td className="px-4 py-2 text-right">
        {error instanceof Error && (
          <span className="text-xs text-red-600 mr-2">{error.message}</span>
        )}
        {user.status !== 'disabled' && (
          <Button
            variant="ghost"
            onClick={() => {
              if (confirm('Neue Einladungs-Mail versenden? Aktueller Token wird ungueltig.'))
                reinvite.mutate();
            }}
            disabled={reinvite.isPending}
          >
            Re-Invite
          </Button>
        )}
        {user.status !== 'disabled' && (
          <Button
            variant="ghost"
            onClick={() => {
              if (confirm(`Zugang fuer ${user.email} wirklich deaktivieren?`)) disable.mutate();
            }}
            disabled={disable.isPending}
          >
            Deaktivieren
          </Button>
        )}
      </td>
    </tr>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
